import { GraphQLFormattedError } from 'graphql';
import { isEmpty, isNil } from 'lodash';

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMBER_PATTERN = /\d+/g;

export const UNKNOWN_OPERATION = 'UnknownOperation';

type ApolloErrorLike = Error & {
  graphQLErrors?: ReadonlyArray<Pick<GraphQLFormattedError, 'message' | 'extensions'>>;
  // Apollo's ServerError/ServerParseError carry the HTTP status of the failed response.
  networkError?: (Error & { statusCode?: number }) | null;
};

// Backend error messages interpolate ids ("Entity <uuid> not found", "Channel with id 123
// not found"). Strip them so every instance of the same error shape shares one fingerprint
// instead of creating a Sentry issue per entity.
export function normalizeGraphQLErrorMessage(message: string): string {
  return message.replace(UUID_PATTERN, '<uuid>').replace(NUMBER_PATTERN, '<n>');
}

export function graphqlErrorFingerprint(
  operationName: string | undefined,
  error: Pick<GraphQLFormattedError, 'message' | 'extensions'>,
): string[] {
  const code = error.extensions?.code;
  const errorKey =
    typeof code === 'string' && !isEmpty(code) ? code : normalizeGraphQLErrorMessage(error.message);
  return ['graphql-error', operationNameOrFallback(operationName), errorKey];
}

export function isApolloErrorLike(error: unknown): error is ApolloErrorLike {
  return (
    error instanceof Error &&
    (error.name === 'ApolloError' || Array.isArray((error as ApolloErrorLike).graphQLErrors))
  );
}

// An ApolloError's stack is entirely node_modules (zen-observable -> asyncMap -> new
// ApolloError), so Sentry groups every one of them into a single issue. Fingerprint by the
// error shape instead; the ApolloError carries no operation name to key on. The HTTP status
// is kept out of the message by normalization, so append it to keep a 500 from grouping
// with a 403. Unhandled rejections are marked so they stay separate from the same error
// caught and reported by a call site.
export function apolloErrorFingerprint(
  error: ApolloErrorLike,
  { unhandled = false }: { unhandled?: boolean } = {},
): string[] {
  const message = normalizeGraphQLErrorMessage(error.message);
  const fingerprint = unhandled
    ? ['apollo-error', 'unhandled', message]
    : ['apollo-error', message];
  const statusCode = error.networkError?.statusCode;
  return isNil(statusCode) ? fingerprint : [...fingerprint, String(statusCode)];
}

export function operationNameOrFallback(operationName: string | undefined): string {
  return isEmpty(operationName) ? UNKNOWN_OPERATION : (operationName as string);
}
